import {
  Box,
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { BrandsContext } from "contexts/Brands";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { ReactNode, useContext } from "react";
import { BentoBrand } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { useCollection } from "utils/useCollection";

import BrandMetadata from "../DetailedBrandView/components/BrandMetadata";
import BrandCardActions from "../Results/BrandCard/BrandCardActions";
import { BrandCardSource } from "../Results/schema";
import BrandIcons from "./BrandIcons";
import BrandLogo from "./BrandLogo";
import BrandSocialLinks from "./BrandSocialLinks";
import PitchedIcon from "./PitchedIcon";

const styles = {
  brandName: (inCardView?: boolean, source?: BrandCardSource) => ({
    fontSize: source === BrandCardSource.THREAD_BRAND ? 24 : 14,
    fontWeight: 700,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    maxWidth: {
      xs: inCardView ? 225 : 200,
      sm: inCardView ? 270 : 425,
      lg: inCardView ? 270 : 600,
      xl: inCardView ? 270 : 800,
    },
  }),
  draft: {
    color: "error.main",
  },
  reco: {
    color: "info.main",
  },
  about: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    WebkitLineClamp: 2,
    textOverflow: "ellipsis",
    fontSize: 12,
  },
  aboutExpanded: {
    fontSize: 12,
  },
  reportWrongInfoButton: (source: BrandCardSource) => ({
    fontSize: 9,
    color: red[300],
    textTransform: "none",
    mt: { xs: 1, md: source === BrandCardSource.THREAD_BRAND ? 1 : 0 },
  }),
};

interface Props {
  bentoBrand: BentoBrand;
  hideMetadata?: boolean;
  source: BrandCardSource;
  expanded?: boolean;
  isCardView?: boolean;
  hideLogo?: boolean;
  actions?: ReactNode;
  hideWrongInfoButton?: boolean;
}

export default function BrandTitle({
  bentoBrand,
  hideMetadata,
  source,
  expanded,
  isCardView,
  actions,
  hideLogo = false,
  hideWrongInfoButton = false,
}: Props) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { lowerWidth } = useContext(DiscoverViewContext);
  const { isDraftBrand, isAutogeneratedDraftBrand } = useCollection();
  const isDrafted = isDraftBrand(bentoBrand);
  const isAutogeneratedDraft = isAutogeneratedDraftBrand(bentoBrand);
  const { setOpenReportInfoDialog } = useContext(BrandsContext);

  const pitchedIconScreenBreak = isMediumScreen || lowerWidth;

  return (
    <Grid item container xs>
      <Grid
        item
        xs={12}
        container
        gap={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Grid
          item
          xs={source ? 9 : 12}
          container
          sx={{ mb: 1 }}
          rowGap={0.5}
          columnGap={1}
          alignItems="center"
        >
          {!hideLogo && <BrandLogo bentoBrand={bentoBrand} />}
          <Box sx={styles.brandName(isCardView, source)}>
            {bentoBrand.normalizedName || bentoBrand.brandName}
            {(isDrafted || isAutogeneratedDraft) && (
              <>
                ,{" "}
                {isAutogeneratedDraft ? (
                  <Box component="span" sx={styles.reco}>
                    Reco{" "}
                    <Box
                      component="i"
                      className="fa-solid fa-wand-magic-sparkles"
                    />
                  </Box>
                ) : (
                  <Box component="span" sx={styles.draft}>
                    Draft
                  </Box>
                )}
              </>
            )}
          </Box>
          {!hideMetadata && <BrandIcons bentoBrand={bentoBrand} />}
          {!isMobileScreen && (
            <BrandSocialLinks
              instagramFollowerCount={bentoBrand.instagramFollowerCount}
              instagramUsername={bentoBrand.instagramUsername}
              bentoBrandId={bentoBrand.id}
              website={bentoBrand.website}
              source={source}
            />
          )}
        </Grid>

        {source !== BrandCardSource.CAMPAIGN_BRAND &&
          source !== BrandCardSource.THREAD_BRAND &&
          !actions && (
            <Grid
              item
              xs
              container
              alignItems="center"
              justifyContent="flex-end"
              rowGap={1}
            >
              {!pitchedIconScreenBreak && bentoBrand.emailedBrand && (
                <Grid item xs="auto" sx={{ mr: 1 }}>
                  <PitchedIcon />
                </Grid>
              )}
              <BrandCardActions source={source} brand={bentoBrand} />
            </Grid>
          )}
        {source === BrandCardSource.CAMPAIGN_BRAND && actions && (
          <Grid item xs container justifyContent="flex-end">
            {actions}
          </Grid>
        )}
      </Grid>

      {isMobileScreen && (
        <Box sx={{ mb: 1 }}>
          <BrandSocialLinks
            instagramFollowerCount={bentoBrand.instagramFollowerCount}
            instagramUsername={bentoBrand.instagramUsername}
            bentoBrandId={bentoBrand.id}
            website={bentoBrand.website}
            source={source}
          />
        </Box>
      )}

      {pitchedIconScreenBreak && bentoBrand.emailedBrand && (
        <Box sx={{ ml: isMobileScreen ? 1 : 0, mb: isMobileScreen ? 0 : 1 }}>
          <PitchedIcon size="small" color="green" />
        </Box>
      )}

      {bentoBrand.aboutSummary && (
        <Grid
          item
          xs={12}
          sx={{
            mb: 1,
          }}
        >
          <Typography
            color="textSecondary"
            sx={[
              expanded ? styles.aboutExpanded : styles.about,
              source === BrandCardSource.THREAD_BRAND && { cursor: "pointer" },
            ]}
          >
            {bentoBrand.aboutSummary}
          </Typography>
        </Grid>
      )}

      {!hideMetadata && (
        <Grid
          container
          item
          xs={12}
          sx={{ my: 0.5 }}
          justifyContent="space-between"
        >
          <BrandMetadata selectedBrand={bentoBrand} source={source} />
          {source !== BrandCardSource.SIMILAR_BRANDS &&
            source !== BrandCardSource.ONBOARDING_BRAND &&
            source !== BrandCardSource.CAMPAIGN_BRAND &&
            !hideWrongInfoButton &&
            !!bentoBrand?.id && (
              <Button
                size="small"
                sx={styles.reportWrongInfoButton(source)}
                onClick={(e: CustomEvent) => {
                  e.stopPropagation();
                  setOpenReportInfoDialog(bentoBrand);
                }}
              >
                Report Wrong Information
              </Button>
            )}
        </Grid>
      )}

      {source !== BrandCardSource.CAMPAIGN_BRAND && actions && (
        <Grid container item xs={12} sx={{ mt: 1 }} justifyContent="flex-end">
          {actions}
        </Grid>
      )}
    </Grid>
  );
}
